// ════════════════════════════════════════════════════════════════════
// src/storeVisits/form-short.js  —  INTEGRATED (Sprint 2.3 part 5)
// v113.x: re-skinned to the shared iOS-modern chrome (svrChrome.js).
//         LOGIC IS UNCHANGED — only render() / ragButtons() / the submit
//         success screen changed. Persistence, autosave, submit gating,
//         and the photo slot all behave exactly as before.
//
// Short visit: metadata + overall only (no 14 sections, no shift embeds).
// Single scrolling page. Same persistence layer as form-detailed.js:
//   - Draft created once a store is entered (POST /api/store-visits)
//   - Autosave: debounced + 30s (PATCH /api/store-visits/:id)
//   - Submit: gated on store + entry_time + overall_rag → status='submitted'
//   - 'overall' photo section via renderPhotoUpload()
//
// visit_type is fixed to 'short' (the choice was made at launch).
//
// Re: `DB` — bare DB. entry_time/exit_time tagged +04:00 (Dubai).
// ════════════════════════════════════════════════════════════════════

import { FINAL_FIELDS } from './form-spec.js';
import { renderPhotoUpload, fetchPhotos as fetchVisitPhotos } from './photoUpload.js';
import { loadStores, loadStaff, storeOptions, dutyOptions } from './storeStaffData.js';
import { injectSvrStyles, svrHeaderHtml } from './svrChrome.js';
import { captureGeo, geoFields } from './geo.js';
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

const RAG_COLORS = {
  green: { bg: '#10b981', label: 'Green' },
  amber: { bg: '#f59e0b', label: 'Amber' },
  red:   { bg: '#ef4444', label: 'Red'   },
};
const AUTOSAVE_INTERVAL_MS = 30000;
const AUTOSAVE_DEBOUNCE_MS = 2000;

function getToken() { return adminToken(); }   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function dubaiLocalToIso(localStr) {
  if (!localStr) return null;
  let s = localStr;
  if (/T\d\d:\d\d$/.test(s)) s += ':00';
  return s + '+04:00';
}
// Inverse — a stored ISO timestamp back to the naive "YYYY-MM-DDTHH:MM" the
// datetime-local input wants, in Dubai wall-clock. Used to re-open a report for
// editing without shifting the displayed time. Mirrors form-detailed.js. v126.1
function isoToDubaiLocal(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
    const time = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: false });
    return date + 'T' + time;
  } catch (e) { return ''; }
}
// A 401/403 means the manager's 24h admin session has lapsed (no sliding
// refresh — see /api/admin/login in worker.js). Tag it so the form can show a
// clear "sign in again" prompt instead of the raw "Unauthorized" string. Mirrors
// form-detailed.js. Fixes the area-manager "Unauthorized" SVR reports.
function sessionExpiredError() {
  const e = new Error('SESSION_EXPIRED');
  e.sessionExpired = true;
  return e;
}
// v136.27: a 403 is NOT an expired session (that is a 401): the account is signed in but may not change
// this report. Mirrors form-detailed.js: the reason is shown, and signing in again is not offered.
async function notAllowedError(res) {
  let m = '';
  try { const d = await res.json(); m = (d && (d.error || d.message)) || ''; } catch (e) { /* no body */ }
  const e = new Error(m || 'This account cannot change this report. Nothing was changed.');
  e.notAllowed = true;
  return e;
}
async function readApi(res) {
  if (res.status === 401) throw sessionExpiredError();
  if (res.status === 403) throw await notAllowedError(res);
  if (!res.ok) { const t = await res.text().catch(function(){return '';}); throw new Error(res.status + ': ' + t.slice(0,160)); }
  return res.json();
}
async function apiPost(p, payload) {
  const res = await fetch(getWorkerUrl() + p, {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return readApi(res);
}
async function apiPatch(p, payload) {
  const res = await fetch(getWorkerUrl() + p, {
    method: 'PATCH',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return readApi(res);
}

// Segmented RAG control — iOS feel. Selected button fills with its RAG colour.
function ragButtons(currentValue) {
  return '<div class="svr-seg">' + ['green', 'amber', 'red'].map(function (rag) {
    const sel = currentValue === rag;
    const style = sel
      ? 'background:' + RAG_COLORS[rag].bg + ';border-color:' + RAG_COLORS[rag].bg + ';color:#fff;'
      : '';
    return '<button type="button" class="svr-seg-btn svr-short-rag" data-value="' + rag + '"' +
      (style ? ' style="' + style + '"' : '') + '>' + RAG_COLORS[rag].label + '</button>';
  }).join('') + '</div>';
}


// renderShortForm(rootEl, opts)
//   opts.visitId / opts.existingData — when set, opens in EDIT mode against an
//   existing short report (re-open a completed report to correct it). AM/HO only.
//   v126.1
export function renderShortForm(rootEl, opts) {
  if (!rootEl) { console.warn('[form-short] rootEl required'); return; }
  opts = opts || {};
  const editMode = !!opts.visitId;
  const existing = opts.existingData || {};
  const prevStatus = existing.status || null;
  // v124.1: a DRAFT re-opened for editing is not the same as editing a COMPLETED
  // report. A draft must stay finishable — you can Submit it (finalise) or Delete it
  // (bin an abandoned one) so it isn't stuck as a draft forever. Completed reports
  // keep the "Save changes" correction flow.
  const editingDraft = editMode && prevStatus === 'draft';

  // iOS-modern page surface (overrides the launcher overlay's white/padding)
  injectSvrStyles();
  rootEl.classList.add('svr-page');
  rootEl.style.background = 'var(--bg)';
  rootEl.style.padding = '0';

  const formState = {
    metadata: { visit_type: 'short' },
    photos: { overall: [] },
    overall_rag: null,
    action_plan: '',
  };
  if (editMode) {
    formState.metadata = {
      visit_type: 'short',
      store: existing.store || '',
      duty_manager: existing.duty_manager || '',
      entry_time: isoToDubaiLocal(existing.entry_time),
      exit_time: isoToDubaiLocal(existing.exit_time),
    };
    formState.overall_rag = existing.overall_rag || null;
    formState.action_plan = existing.action_plan || '';
  }
  let visitId = opts.visitId || null;
  let editDirty = false;   // edit mode only: true once the AM changes something unsaved

  // ── Store + duty-manager dropdown data (cached, loaded once) ──
  let storesData = null;
  let staffData = null;
  (async function loadMeta() {
    try {
      const [st, sf] = await Promise.all([loadStores(), loadStaff()]);
      storesData = st;
      staffData = sf;
      render();   // populate the selects
    } catch (e) {
      console.warn('[form-short] store/staff load failed', e);
    }
  })();

  let saving = false, dirty = false, debounceTimer = null, intervalId = null;
  let authExpired = false;   // set once the admin session lapses mid-report (E1)

  function buildPayload() {
    return {
      store: formState.metadata.store,
      visit_type: 'short',
      duty_manager: formState.metadata.duty_manager || null,
      entry_time: dubaiLocalToIso(formState.metadata.entry_time),
      exit_time: dubaiLocalToIso(formState.metadata.exit_time),
      overall_rag: formState.overall_rag,
      action_plan: formState.action_plan || null,
    };
  }
  function setSaveStatus(text, colour) {
    const el = rootEl.querySelector('#svr-save-status');
    if (el) { el.textContent = text; el.style.color = colour || 'var(--learn-grey)'; }
  }
  function markDirty() {
    if (authExpired) return;   // session is dead — don't queue more dead-token saves
    if (editMode) {
      // Editing a completed report: NO autosave — changes persist only on an explicit
      // "Save changes" (one PATCH + one audit row per edit session). v126.1
      editDirty = true;
      updateEditSaveButton();
      return;
    }
    dirty = true;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doSave, AUTOSAVE_DEBOUNCE_MS);
  }
  function updateEditSaveButton() {
    const btn = rootEl.querySelector('#svr-edit-save');
    if (!btn) return;
    btn.disabled = !editDirty;
    btn.textContent = editDirty ? 'Save changes' : 'Saved';
    btn.style.opacity = editDirty ? '1' : '0.55';
  }
  async function doSave() {
    if (authExpired || !visitId || !dirty) return;
    if (saving) { dirty = true; return; }
    saving = true; dirty = false;
    setSaveStatus('Saving…');
    try {
      await apiPatch('/api/store-visits/' + visitId, buildPayload());
      const t = new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' });
      setSaveStatus('Saved ' + t, 'var(--ok)');
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      if (e && e.notAllowed) { teardown(); setSaveStatus(e.message, 'var(--ui-bad)'); return; }   // retrying cannot help
      dirty = true; setSaveStatus('Save failed — will retry', 'var(--ui-bad)');
      console.warn('[form-short] autosave failed:', e.message);
    } finally {
      saving = false;
      if (dirty && !authExpired) setTimeout(doSave, 1000);
    }
  }
  async function createDraftIfNeeded() {
    if (visitId || !formState.metadata.store) return;
    const visit = await apiPost('/api/store-visits', {
      store: formState.metadata.store,
      visit_type: 'short',
      duty_manager: formState.metadata.duty_manager || null,
      entry_time: dubaiLocalToIso(formState.metadata.entry_time),
      status: 'draft',
    });
    visitId = visit.id;
    if (!intervalId) intervalId = setInterval(doSave, AUTOSAVE_INTERVAL_MS);
    setSaveStatus('Draft started', 'var(--ok)');
    // Re-render the photo slot now that a visit_id exists
    populatePhotoSlot();
  }
  function teardown() {
    clearTimeout(debounceTimer);
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
  }

  // v124.1: delete a DRAFT from the edit view (AM/HO). Completed reports are deleted
  // from the reports list; this is only for an unfinished draft so it isn't stuck
  // forever. Soft-delete via the same DELETE the list uses; closes + refreshes on success.
  async function deleteDraft() {
    if (!visitId) return;
    if (typeof window.confirm === 'function' &&
        !window.confirm('Delete this draft report? This cannot be undone.')) return;
    setSubmitMsg('Deleting…', 'var(--learn-grey)');
    try {
      const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(visitId), { cache: 'no-store',
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + getToken() },
      });
      if (res.status === 401) throw sessionExpiredError();
      if (res.status === 403) throw await notAllowedError(res);
      if (!res.ok) { const t = await res.text().catch(function () { return ''; }); throw new Error(res.status + ': ' + t.slice(0, 160)); }
      teardown();
      try { window.dispatchEvent(new Event('svr:saved')); } catch (e) { /* list refresh is best-effort */ }
      if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      setSubmitMsg('Delete failed: ' + e.message, 'var(--ui-bad)');
    }
  }

  // ── E1: graceful handling when the 24h admin session lapses mid-report ──
  // Mirrors form-detailed.js: pause autosave, keep answers on screen, stash a
  // best-effort local backup, and show a clear "sign in again" banner instead
  // of the old raw "Unauthorized" + endless silent retry.
  function backupLocally() {
    try {
      localStorage.setItem('svr_draft_backup', JSON.stringify({
        visitId: visitId,
        metadata: formState.metadata,
        overall_rag: formState.overall_rag,
        action_plan: formState.action_plan,
        ts: Date.now(),
      }));
    } catch (e) { /* storage disabled/full — best-effort recovery copy only */ }
  }
  function onSessionExpired() {
    if (authExpired) return;
    authExpired = true;
    teardown();
    backupLocally();
    setSaveStatus('Session expired — saving paused', 'var(--ui-bad)');
    showSessionExpiredBanner();
  }
  function showSessionExpiredBanner() {
    if (rootEl.querySelector('#svr-auth-banner')) return;
    const bar = document.createElement('div');
    bar.id = 'svr-auth-banner';
    bar.style.cssText = 'position:sticky;top:0;z-index:50;background:#b91c1c;color:#fff;' +
      'padding:12px 16px;font-size:14px;line-height:1.45;display:flex;gap:12px;' +
      'align-items:center;flex-wrap:wrap;';
    const msg = document.createElement('span');
    msg.style.cssText = 'flex:1;min-width:200px;';
    msg.textContent = 'Your session has expired, so saving is paused. Your answers are ' +
      'still on screen — please note anything important, then sign in again to continue.';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Sign in again';
    btn.style.cssText = 'background:var(--card);color:var(--danger-deep);border:none;border-radius:8px;' +
      'padding:8px 14px;font-weight:600;cursor:pointer;font-family:inherit;flex:0 0 auto;';
    btn.addEventListener('click', function () { try { location.reload(); } catch (e) { /* noop */ } });
    bar.appendChild(msg);
    bar.appendChild(btn);
    rootEl.insertBefore(bar, rootEl.firstChild);
  }

  function setSubmitMsg(text, colour) {
    const el = rootEl.querySelector('#svr-submit-msg');
    if (el) { el.textContent = text; el.style.color = colour || 'var(--learn-grey)'; }
  }
  async function submit() {
    const missing = [];
    if (!formState.metadata.store) missing.push('Store');
    if (!formState.metadata.entry_time) missing.push('Entry time');
    if (!formState.overall_rag) missing.push('Overall RAG');
    if (missing.length) { setSubmitMsg('Please complete: ' + missing.join(', '), 'var(--ui-bad)'); return; }
    setSubmitMsg('Submitting…', 'var(--learn-grey)');
    try {
      await createDraftIfNeeded();   // in case store was set but draft not yet created
      dirty = true; await doSave();
      // Geotag: record WHERE the AM completed the report (permission-prompted; never
      // blocks the submit — null if denied/unavailable). No geofence by design.
      const geo = await captureGeo();
      await apiPatch('/api/store-visits/' + visitId, Object.assign(buildPayload(), { status: 'submitted' }, geoFields(geo)));
      teardown();
      rootEl.innerHTML =
        '<div class="svr-wrap"><div class="svr-done-wrap">' +
          '<div style="font-size:54px;">✅</div>' +
          '<h2 style="color:var(--text);margin:8px 0 4px;">Short visit submitted</h2>' +
          '<p style="color:var(--learn-grey);margin:0 0 20px;">' + escapeHtml(formState.metadata.store) + ' — overall ' + escapeHtml(formState.overall_rag) + '.</p>' +
          '<button type="button" id="svr-done" class="svr-btn-primary" style="max-width:200px;margin:0 auto;">Done</button>' +
        '</div></div>';
      const done = rootEl.querySelector('#svr-done');
      if (done) done.addEventListener('click', function () { if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl); });
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      setSubmitMsg('Submit failed: ' + e.message, 'var(--ui-bad)');
    }
  }

  function populatePhotoSlot() {
    const slot = rootEl.querySelector('#svr-short-photos');
    if (!slot) return;
    renderPhotoUpload({
      visitId: visitId,
      sectionKey: 'overall',
      container: slot,
      photos: formState.photos.overall || [],
      onChange: function (photos) { formState.photos.overall = photos; },
    });
  }

  // ── Edit mode (re-open a completed short report) ──
  async function loadExistingPhotos() {
    try {
      const all = await fetchVisitPhotos(visitId);
      formState.photos.overall = (all || []).filter(function (p) {
        return (p.section_key || 'overall') === 'overall';
      });
      populatePhotoSlot();
    } catch (e) {
      console.warn('[form-short] existing photos load failed', e);
    }
  }

  async function saveEdit() {
    if (!visitId) return;
    if (!formState.metadata.store) { setSubmitMsg('Store is required', 'var(--ui-bad)'); return; }
    const headerBtn = rootEl.querySelector('#svr-edit-save');
    if (headerBtn) { headerBtn.disabled = true; headerBtn.textContent = 'Saving…'; }
    setSubmitMsg('Saving changes…', 'var(--learn-grey)');
    try {
      // status NOT sent → a reviewed report stays reviewed; _audit_edit asks the Worker
      // to write one svr_edit audit row. v126.1
      await apiPatch('/api/store-visits/' + visitId, Object.assign(buildPayload(), {
        _audit_edit: true,
        _prev_status: prevStatus,
      }));
      editDirty = false;
      teardown();
      try { window.dispatchEvent(new CustomEvent('svr:saved')); } catch (e) { /* noop */ }
      rootEl.innerHTML =
        '<div class="svr-wrap"><div class="svr-done-wrap">' +
          '<div style="font-size:54px;">✅</div>' +
          '<h2 style="color:var(--text);margin:8px 0 4px;">Changes saved</h2>' +
          '<p style="color:var(--learn-grey);margin:0 0 20px;">' + escapeHtml(formState.metadata.store) + ' — report updated.</p>' +
          '<button type="button" id="svr-done" class="svr-btn-primary" style="max-width:200px;margin:0 auto;">Done</button>' +
        '</div></div>';
      const done = rootEl.querySelector('#svr-done');
      if (done) done.addEventListener('click', function () { if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl); });
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      setSubmitMsg('Save failed: ' + e.message, 'var(--ui-bad)');
      if (headerBtn) { headerBtn.disabled = false; headerBtn.textContent = 'Save changes'; }
    }
  }

  function render() {
    const m = formState.metadata;
    const headerRight = editMode
      ? '<button type="button" id="svr-edit-save" style="min-height:34px;padding:0 14px;border:none;' +
          'background:#16a34a;color:#fff;border-radius:9px;font-size:13px;font-weight:700;font-family:inherit;' +
          'cursor:pointer;opacity:' + (editDirty ? '1' : '0.55') + ';"' + (editDirty ? '' : ' disabled') + '>' +
          (editDirty ? 'Save changes' : 'Saved') + '</button>' +
        '<button type="button" id="svr-close" class="svr-close" aria-label="Close">✕</button>'
      : '<div id="svr-save-status" class="svr-status">' + (visitId ? 'Draft saved' : 'Not saved yet') + '</div>' +
        '<button type="button" id="svr-close" class="svr-close" aria-label="Close">✕</button>';

    rootEl.innerHTML =
      svrHeaderHtml({ subtitle: editMode ? 'Editing short report' : 'Short visit', right: headerRight }) +
      '<div class="svr-wrap">' +

        '<div class="svr-section-title">Visit details</div>' +
        '<div class="svr-card">' +
          '<div class="svr-field"><label class="svr-label">Store <span class="svr-req">*</span></label>' +
            '<select class="svr-input svr-short-meta" data-field="store">' + storeOptions(storesData, m.store) + '</select></div>' +
          '<div class="svr-field"><label class="svr-label">Duty manager</label>' +
            '<select class="svr-input svr-short-meta" data-field="duty_manager"' + (m.store ? '' : ' disabled') + '>' + dutyOptions(staffData, m.store, m.duty_manager) + '</select></div>' +
          '<div class="svr-field"><label class="svr-label">Entry time <span class="svr-req">*</span></label>' +
            '<input type="datetime-local" class="svr-input svr-short-meta" data-field="entry_time" value="' + escapeHtml(m.entry_time || '') + '"></div>' +
          '<div class="svr-field"><label class="svr-label">Exit time</label>' +
            '<input type="datetime-local" class="svr-input svr-short-meta" data-field="exit_time" value="' + escapeHtml(m.exit_time || '') + '"></div>' +
        '</div>' +

        '<div class="svr-section-title">Overall</div>' +
        '<div class="svr-card">' +
          '<div class="svr-field"><label class="svr-label">' + escapeHtml(FINAL_FIELDS.overall_rag.label) + ' <span class="svr-req">*</span></label>' +
            ragButtons(formState.overall_rag) + '</div>' +
          '<div class="svr-field"><label class="svr-label">' + escapeHtml(FINAL_FIELDS.action_plan.label) + '</label>' +
            '<textarea id="svr-short-plan" class="svr-input" rows="5" maxlength="2000">' + escapeHtml(formState.action_plan || '') + '</textarea></div>' +
          '<div class="svr-field"><label class="svr-label">Upload pictures of the store</label>' +
            '<div id="svr-short-photos"></div></div>' +
        '</div>' +

        '<button type="button" id="svr-submit" class="svr-btn-primary">' +
          ((editMode && !editingDraft) ? 'Save changes' : 'Submit visit') + '</button>' +
        (editingDraft
          ? '<button type="button" id="svr-delete-draft" style="margin-top:10px;width:100%;min-height:44px;border:1px solid #dc2626;background:var(--card);color:var(--ui-bad);border-radius:10px;font-size:15px;font-weight:700;font-family:inherit;cursor:pointer;">Delete draft</button>'
          : '') +
        '<div id="svr-submit-msg" class="svr-submit-msg"></div>' +
      '</div>';

    // RAG
    rootEl.querySelectorAll('.svr-short-rag').forEach(function (btn) {
      btn.addEventListener('click', function () { formState.overall_rag = btn.dataset.value; markDirty(); render(); });
    });
    // Metadata
    rootEl.querySelectorAll('.svr-short-meta').forEach(function (el) {
      const field = el.dataset.field;
      if (el.tagName === 'SELECT') {
        el.addEventListener('change', async function () {
          formState.metadata[field] = el.value;
          markDirty();
          if (field === 'store') {
            formState.metadata.duty_manager = '';   // new store → different staff
            render();                                 // refresh duty-manager options
            if (!visitId && el.value.trim()) {
              try { await createDraftIfNeeded(); }
              catch (e) {
                if (e && e.sessionExpired) { onSessionExpired(); }
                else setSaveStatus('Could not start visit: ' + e.message, 'var(--ui-bad)');
              }
            }
          }
        });
      } else {
        el.addEventListener('input', function () { formState.metadata[field] = el.value; markDirty(); });
      }
    });
    // Action plan
    const plan = rootEl.querySelector('#svr-short-plan');
    if (plan) plan.addEventListener('input', function () { formState.action_plan = plan.value; markDirty(); });
    // Primary action: Submit (create OR finishing a re-opened draft) / Save changes
    // (correcting a completed report). A draft must stay submittable, not stuck.
    const sub = rootEl.querySelector('#svr-submit');
    if (sub) sub.addEventListener('click', (editMode && !editingDraft) ? saveEdit : submit);
    // Delete draft (edit mode, draft only) — bin an abandoned draft so it isn't stuck forever.
    const delDraftBtn = rootEl.querySelector('#svr-delete-draft');
    if (delDraftBtn) delDraftBtn.addEventListener('click', deleteDraft);
    // Persistent header Save button (edit mode only)
    const editSaveBtn = rootEl.querySelector('#svr-edit-save');
    if (editSaveBtn) editSaveBtn.addEventListener('click', saveEdit);
    // Close
    const close = rootEl.querySelector('#svr-close');
    if (close) close.addEventListener('click', function () {
      if (editMode && editDirty && !window.confirm('Discard your unsaved changes to this report?')) return;
      teardown();
      if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
    });

    // Photo slot (shows "save first" until a draft exists)
    populatePhotoSlot();

    // render() rebuilds rootEl.innerHTML, which drops the expiry banner — re-show
    // it so it survives any re-render after the session lapsed.
    if (authExpired) showSessionExpiredBanner();
  }

  render();
  // Edit mode: pull the report's existing photos in once the form is on screen.
  if (editMode) loadExistingPhotos();
}

window.renderShortForm = renderShortForm;
